import { Box, IconButton, MenuItem, TextField, TextFieldProps } from '@mui/material';
import React, { ForwardedRef, forwardRef, useState } from 'react';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import CloseIcon from '@mui/icons-material/Close';
import i18n from '../../i18n';
import './scss/base-input.style.scss';
import { useTranslation } from 'react-i18next';

/**
 * 输入框组件
 * - regularType 正则检验
 */
// eslint-disable-next-line react/display-name
const BaseInput = forwardRef((props: BaseTextFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
    useTranslation()
    // isEmpty：文本为空时是否校验 (false表示不校验)，保存时才校验必填且为空的值
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isEmpty = false, value = '', regularType, allowClear, onChange, onBlur, label, style, items, select, ...otherProps } = props;
    // 记录文本信息
    const [textValue, setTextValue] = useState<BaseTextFieldProps['value']>(value);
    const [counter, setCounter] = useState<number>(0);
    const [helperTextValue, setHelperTextValue] = useState<string>('');
    function getError() {
        /**
         * isEmpty：文本为空时是否校验
         * counter: 输入款失去焦点
         */
        if (isEmpty || counter) {
            if (String(textValue)?.trim()) {
                if (regularType && Object.prototype.hasOwnProperty.call(BASE_CHECK_TYPE, regularType)) {
                    const regular = BASE_CHECK_TYPE[regularType]?.regular;
                    return !regular.test(String(textValue))
                }
            } else {
                return props.required
            }
        } else {
            return false
        }
    }
    React.useEffect(() => {
        if (regularType && Object.prototype.hasOwnProperty.call(BASE_CHECK_TYPE, regularType)) {
            setHelperTextValue(i18n.t(BASE_CHECK_TYPE[regularType]?.helperText, BASE_CHECK_TYPE[regularType]?.i18nRule))
        } else {
            setHelperTextValue(i18n.t('address_component_text_please_input'))
        }
    }, [regularType]);

    React.useEffect(() => {
        setTextValue(value)
    }, [value]);

    React.useEffect(() => {
        if (!isEmpty) {
            setCounter(0)
        }
    }, [isEmpty]);

    React.useEffect(() => {
        if (getError()) {
            if (regularType && Object.prototype.hasOwnProperty.call(BASE_CHECK_TYPE, regularType)) {
                setHelperTextValue(i18n.t(BASE_CHECK_TYPE[regularType]?.helperText, BASE_CHECK_TYPE[regularType]?.i18nRule))
            } else {
                setHelperTextValue(i18n.t('address_component_text_please_input'))
            }
        }
    }, [i18n.language])

    /**
     * 数字输入框只能输入1-9
     * @param event
     */
    const onNumberInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const { key } = event;
        const filteredKeys = new Set(['e', '-', '+', 'E']);
        if (filteredKeys.has(key)) {
            event.preventDefault();
        }
    };

    // *********************
    // 视图区
    // *********************
    return (
        <Box width='100%' position='relative' className={`base-input ${props.value && allowClear ? 'allow-clear' : ''}`}>
            <TextField
                ref={ref}
                className={props.className}
                onBlur={(e) => {
                    setCounter(1);
                    onBlur?.(e);
                }}
                autoComplete={props.autoComplete}
                autoFocus={props.autoFocus}
                color={props.color}
                disabled={props.disabled}
                error={getError()}
                FormHelperTextProps={props.FormHelperTextProps}
                fullWidth={props.fullWidth}
                id={props.id}
                // 错误文本
                helperText={getError() ? (props?.helperText ?? helperTextValue ?? '') : ''}
                InputLabelProps={props.InputLabelProps}
                InputProps={props.InputProps}
                inputProps={props.inputProps}
                inputRef={props.inputRef}
                label={typeof label === 'string' ? i18n.t(label as string) : label}
                margin={props.margin}
                multiline={props.multiline}
                name={props.name}
                onChange={(event) => {
                    if (props.maxLength ? (event.target.value.length <= props.maxLength) : true) {
                        setTextValue(event.target.value);
                        props?.onChange?.(event);
                    }
                }}
                placeholder={props.placeholder}
                required={props.required}
                // rows={props.rows}
                // rowsMax={props.rowsMax}
                select={props.select}
                SelectProps={props.SelectProps}
                size={props.size || 'small'}
                style={{
                    minHeight: props.helperText ? 75 : 'auto',
                    imeMode: 'disabled',
                    ...props.style
                }}
                type={props.type}
                value={props.value}
                variant={props.variant ?? 'outlined'}
                onKeyDown={(event) => props.type === 'number' ? onNumberInputKeyDown(event) : undefined}
                {...otherProps}
            >
                {
                    props.select ?
                        (props.items ?? []).map((option, index) => (
                            props.SelectProps?.native ?
                                <option key={option.value + '_' + index} value={option.value}>
                                    {typeof option.label === 'string' ? option.label : ''}
                                </option>
                                :
                                <MenuItem key={option.value + '_' + index} value={option.value} disabled={option.disabled}>
                                    {typeof option.label === 'string' ? i18n.t(option.label) : option.label}
                                </MenuItem>
                        ))
                        : null
                }
                {
                    props.select && (props.items ?? []).length === 0 &&
                    <MenuItem value='' disabled>
                        {i18n.t('general_text_no_data')}
                    </MenuItem>
                }
            </TextField>
            {
                allowClear &&
                <div className='clear-button'>
                    <IconButton
                        size='small'
                        onClick={() => {
                            const event = {
                                target: {
                                    value: ''
                                }
                            }
                            setTextValue(event.target.value);
                            props?.onChange?.(event as any);
                        }}>
                        <CloseIcon />
                    </IconButton>
                </div>
            }
        </Box>
    )
})

export default BaseInput;

export type BaseTextFieldProps = {
    items?: SelectOption[];
    // isEmpty：文本为空时是否校验 (false表示不校验)，保存时才校验必填且为空的值
    isEmpty?: boolean;
    customStyle?: object;
    value?: string | number | Date | string[] | number[] | SelectOption;
    maxLength?: number;
    textFieldRef?: any;
    // 校验文本规则
    regularType?: 'nickname' |
    'phone' |
    'email' |
    'password' |
    'name' |
    'printName' |
    'staffName' |
    'optionalName' |
    'description' |
    'vatNumber' |
    'zipCode' |
    'recipientCode' |
    'fiscalCode' |
    'pec' |
    'website' |
    'name2' |
    'required' | 'dishName' | string;
    // 支持清除
    allowClear?: boolean;
} & TextFieldProps

// 下拉选项（label 可为 i18n key 字符串，或如图标选择器等自定义节点）
export type SelectOption = {
    value: string | number;
    label: string | React.ReactNode;
    disabled?: boolean;
}

/**
 * 将下拉项 label 转为纯文本，供 Autocomplete、国家列表等仅支持 string 的场景使用。
 * @param label - SelectOption 的 label（字符串或 React 节点）
 * @returns 可展示的字符串；为 React 元素等非原始类型时返回空串（输入框无法稳定嵌入复杂节点）
 */
export function selectOptionLabelToPlainString(label: string | React.ReactNode | undefined | null): string {
    if (label === null || label === undefined) {
        return '';
    }
    if (typeof label === 'string') {
        return label;
    }
    if (typeof label === 'number' || typeof label === 'boolean') {
        return String(label);
    }
    return '';
}